import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../../components/ui/table';
import { Button } from '../../../../components/ui/button';
import {
  ExternalLink,
  Trash2,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  RefreshCw,
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../../../components/ui/alert-dialog';
import type { BlogResponse } from '../../services/blogApi';
import { blogService } from '../../services/blogApi';
import { Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';

interface BlogTableProps {
  blogs: BlogResponse[];
  onRefresh: () => void;
  isLoading?: boolean;
}

type SortField = 'title' | 'file_name' | 'id';
type SortOrder = 'asc' | 'desc';

const TableSkeleton = () => (
  <TableRow>
    <TableCell>
      <div className="h-4 w-3/4 bg-gray-200 rounded animate-pulse"></div>
    </TableCell>
    <TableCell>
      <div className="h-4 w-1/2 bg-gray-200 rounded animate-pulse"></div>
    </TableCell>
    <TableCell>
      <div className="flex gap-2">
        <div className="h-8 w-8 bg-gray-200 rounded animate-pulse"></div>
        <div className="h-8 w-8 bg-gray-200 rounded animate-pulse"></div>
      </div>
    </TableCell>
  </TableRow>
);

const EmptyState = () => (
  <div className="flex flex-col items-center justify-center h-64 text-center">
    <div className="w-16 h-16 bg-background-main rounded-full  text-text-primary flex items-center justify-center mb-4">
      <ExternalLink size={24} />
    </div>
    <h3 className="font-medium">No blogs found</h3>
    <p className="text-sm  mt-2">Create a new blog post to get started</p>
  </div>
);

export const BlogTable: React.FC<BlogTableProps> = ({
  blogs,
  onRefresh,
  isLoading = false,
}) => {
  const [sortConfig, setSortConfig] = useState<{
    field: SortField;
    order: SortOrder;
  }>({
    field: 'id',
    order: 'desc',
  });

  const [deletingId, setDeletingId] = useState<number | null>(null);

  const handleSort = (field: SortField) => {
    setSortConfig((prevConfig) => ({
      field,
      order:
        prevConfig.field === field && prevConfig.order === 'asc'
          ? 'desc'
          : 'asc',
    }));
  };

  const getSortIcon = (field: SortField) => {
    if (sortConfig.field !== field) return <ArrowUpDown size={16} />;
    return sortConfig.order === 'asc' ? (
      <ArrowUp size={16} />
    ) : (
      <ArrowDown size={16} />
    );
  };

  const handleDelete = async () => {
    if (!deletingId) return;

    try {
      const response = await blogService.deleteBlog(deletingId);
      if (response.success) {
        toast.success('Blog deleted successfully');
        onRefresh();
      }
    } catch (error) {
      console.error(error);
      toast.error('Failed to delete blog');
    } finally {
      setDeletingId(null);
    }
  };

  const sortedBlogs = [...blogs].sort((a, b) => {
    const order = sortConfig.order === 'asc' ? 1 : -1;
    switch (sortConfig.field) {
      case 'title':
        return order * (a.title || '').localeCompare(b.title || '');
      case 'file_name':
        return order * (a.file_name || '').localeCompare(b.file_name || '');
      case 'id':
        return order * (a.id - b.id);
      default:
        return 0;
    }
  });

  return (
    <div>
      <div className="flex justify-end mb-4">
        <Button
          variant="customSecondary"
          size="sm"
          onClick={onRefresh}
          className="gap-2 bg-secondary text-primary"
          disabled={isLoading}
        >
          <RefreshCw size={16} className={isLoading ? 'animate-spin' : ''} />
          Refresh
        </Button>
      </div>

      {isLoading || typeof blogs === 'undefined' || blogs === null ? (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Title</TableHead>
              <TableHead>File Name</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            <TableSkeleton />
            <TableSkeleton />
            <TableSkeleton />
          </TableBody>
        </Table>
      ) : blogs.length === 0 ? (
        <EmptyState />
      ) : (
        <Table className="text-text-primary">
          <TableHeader>
            <TableRow className="text-primary">
              <TableHead>
                <button
                  type="button"
                  onClick={() => handleSort('title')}
                  className="flex items-center gap-1 "
                >
                  Title {getSortIcon('title')}
                </button>
              </TableHead>
              <TableHead>
                <button
                  type="button"
                  onClick={() => handleSort('file_name')}
                  className="flex items-center gap-1 hover:text-gray-900"
                >
                  File Name {getSortIcon('file_name')}
                </button>
              </TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedBlogs.map((blog) => (
              <TableRow key={blog.id}>
                <TableCell className="font-medium">{blog.title}</TableCell>
                <TableCell>{blog.file_name}</TableCell>
                <TableCell>
                  <div className="flex gap-2">
                    <Link to={`/ai-blog-writer/blog?id=${blog.id}`}>
                      <Button variant="ghost" size="icon" title="Open">
                        <ExternalLink size={16} />
                      </Button>
                    </Link>
                    <Button
                      variant="ghost"
                      size="icon"
                      title="Delete"
                      onClick={() => setDeletingId(blog.id)}
                      className="hover:text-red-500"
                    >
                      <Trash2 size={16} className="text-red-500" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <AlertDialog
        open={deletingId !== null}
        onOpenChange={(open) => !open && setDeletingId(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete this
              blog.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default BlogTable;
